import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { useSetTenantFeature, useTenantFeatureHistory } from '@/hooks/useTenants'
import { requestErrorMessage } from '@/helpers/apiClient'
import { instantLabel } from '@/lib/instantLabel'
import { Button } from '@/components/ui/button'
import { Table, TableHeader, TableHead, TableBody, TableRow, TableCell, TableEmptyRow } from '@/components/ui/table'
import { Card } from '@/components/ui/card'
import { TENANT_FEATURE_INFO } from '@lib/constants/tenant-features'
import { TENANT_FEATURES, type TenantFeature } from '@lib/enums/tenant-feature.enum'
import type { TenantDto } from '@lib/dto/tenant.dto'

/** One feature's switch: what it is, what it gives, and whether this company has it. */
function FeatureRow({ tenant, feature }: { tenant: TenantDto; feature: TenantFeature }) {
  const setFeature = useSetTenantFeature(tenant.id)
  const [error, setError] = React.useState<string | null>(null)
  const info = TENANT_FEATURE_INFO[feature]
  const enabled = tenant.features.includes(feature)

  async function toggle(next: boolean): Promise<void> {
    setError(null)
    try {
      await setFeature.mutateAsync({ feature, enabled: next })
    } catch (e) {
      setError(requestErrorMessage(e))
    }
  }

  return (
    <Card className="flex items-start justify-between gap-4 p-4">
      <label className="flex items-start gap-3 text-sm">
        <input
          type="checkbox"
          checked={enabled}
          disabled={setFeature.isPending}
          onChange={(e) => void toggle(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-line"
        />
        <span className="space-y-1">
          <span className="block font-medium text-ink">{info.label}</span>
          <span className="block text-ink-muted">{info.description}</span>
          {error ? <span className="block text-danger-ink">{error}</span> : null}
        </span>
      </label>
      {setFeature.isPending ? <Loader2 className="h-4 w-4 animate-spin text-ink-muted" /> : null}
    </Card>
  )
}

/** Every time a feature was switched for this company, newest first. */
function FeatureHistory({ tenantId }: { tenantId: string }) {
  const history = useTenantFeatureHistory(tenantId)

  function renderRows() {
    if (history.isPending) {
      return <TableEmptyRow colSpan={3}>Loading history…</TableEmptyRow>
    }
    if (history.isError) {
      return (
        <TableEmptyRow colSpan={3}>
          {requestErrorMessage(history.error)}{' '}
          <Button variant="link" size="sm" onClick={() => void history.refetch()}>
            Try again
          </Button>
        </TableEmptyRow>
      )
    }
    if (history.data.changes.length === 0) {
      return <TableEmptyRow colSpan={3}>No feature has been switched for this organization yet.</TableEmptyRow>
    }
    return history.data.changes.map((change) => (
      <TableRow key={change.id}>
        <TableCell>
          {TENANT_FEATURE_INFO[change.feature].label} turned {change.enabled ? 'on' : 'off'}
        </TableCell>
        <TableCell>{change.changedBy?.name ?? 'A removed user'}</TableCell>
        <TableCell>{instantLabel(change.createdAt)}</TableCell>
      </TableRow>
    ))
  }

  return (
    <Table heading="History">
      <TableHeader>
        <TableHead>Change</TableHead>
        <TableHead>By</TableHead>
        <TableHead>When</TableHead>
      </TableHeader>
      <TableBody>{renderRows()}</TableBody>
    </Table>
  )
}

/**
 * The super admin's switches for the parts of the product a company has, and the
 * record of who switched what. A change takes effect on the company's next
 * request — the API refuses a switched-off feature, it does not just hide it.
 */
export function TenantFeaturesSection({ tenant }: { tenant: TenantDto }) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {TENANT_FEATURES.map((feature) => (
          <FeatureRow key={feature} tenant={tenant} feature={feature} />
        ))}
      </div>
      <FeatureHistory tenantId={tenant.id} />
    </div>
  )
}
